// channelTool.js — shared "Parallel Channel" tool for both charts (Dashboard's
// SignalChart.js and the More Info chart in App.js). Fourth of the four "advanced
// drawing" tools scoped in the 2026-07-08 handoff (Long Position remains a separate
// future build). This is the first one with a genuinely NEW interaction shape vs.
// Trend Line/Fib's pure click-drag:
//
//   Stage 1 (idle):            click-drag defines the main trend line — identical
//                               gesture to trendLineTool.js.
//   Stage 2 (awaiting-offset): ONE more click sets the channel's width. Uses the
//                               chart's native subscribeClick (same primitive Draw Line
//                               uses for its click-to-place), not the raw mouse
//                               listeners — this is a single click, not a drag, so it
//                               doesn't need pan/zoom suppressed.
//
// Offset math: the second line is offset from the first by a CONSTANT PRICE DELTA
// (delta = price3 − line1's interpolated price at time3), not a true perpendicular
// screen-space offset. Simpler, and the same "good enough" tradeoff this codebase
// already made in volumeProfile.js (bucket-midpoint instead of proportional split) —
// chosen deliberately over true-perpendicular per the founder's own call.
//
// Stage 2 also tracks mouse movement to show a LIVE preview of the second line (and
// its fill) while the user positions the offset, same continuous-feedback feel as
// Trend Line/Fib's single-drag interaction — not just a dead main line until the
// click lands.
//
// Persistence: type="channel", points={time1,price1,time2,price2,time3,price3} — the
// raw third click is stored (not a precomputed delta), matching trendLineTool.js's
// convention of storing raw anchors rather than derived values, so the delta is always
// recomputed fresh from source data. Multi-channel mode: stays armed across multiple
// complete channels, like Draw Line/Trend Line/Fib — only disarms when the button is
// clicked again (or resets mid-flight if disarmed while awaiting an offset click).

import { useState, useRef, useEffect, useCallback } from "react";
import { fetchAdvancedDrawings, insertAdvancedDrawing, deleteAdvancedDrawing } from "./advancedDrawingsStore";

const TYPE = "channel";

// Time↔pixel helpers that extrapolate linearly from the visible range when the native
// coordinateToTime/timeToCoordinate return null — same fix, same rationale as
// fibTool.js's identical helpers (confirmed live: dragging into the whitespace margin
// past the last/first candle made the drawn line stop short of the cursor). Duplicated
// rather than shared, matching this codebase's existing convention of copy-paste-with-
// comment over cross-file abstraction for small per-tool helpers.
function xToTime(chart, x) {
  const ts = chart.timeScale();
  const t = ts.coordinateToTime(x);
  if (t != null) return t;
  const range = ts.getVisibleRange();
  if (!range) return null;
  const xFrom = ts.timeToCoordinate(range.from);
  const xTo = ts.timeToCoordinate(range.to);
  if (xFrom == null || xTo == null || xFrom === xTo) return null;
  return range.from + ((x - xFrom) / (xTo - xFrom)) * (range.to - range.from);
}
function timeToX(chart, time) {
  const ts = chart.timeScale();
  const x = ts.timeToCoordinate(time);
  if (x != null) return x;
  const range = ts.getVisibleRange();
  if (!range) return null;
  const xFrom = ts.timeToCoordinate(range.from);
  const xTo = ts.timeToCoordinate(range.to);
  if (xFrom == null || xTo == null || range.to === range.from) return null;
  return xFrom + ((time - range.from) / (range.to - range.from)) * (xTo - xFrom);
}

export function useChannelTool({ chartRef, seriesRef, containerRef, userId, symbol, context }) {
  const [channelMode, setChannelMode] = useState(false);
  const [myChannels, setMyChannels] = useState([]); // [{id, time1,price1,time2,price2,time3,price3}]
  const [draft, setDraft] = useState(null);          // stage-1 in-progress drag, DATA space
  const [phase, setPhase] = useState("idle");         // "idle" | "awaiting-offset"
  const [pendingLine, setPendingLine] = useState(null); // stage-1 committed line, awaiting stage 2
  const [offsetPreview, setOffsetPreview] = useState(null); // stage-2 live mouse position, DATA space
  const [, setRangeTick] = useState(0);
  const draggingRef = useRef(false);
  const draftRef = useRef(null);
  const pendingLineRef = useRef(null); // mirrors pendingLine — read inside the click handler

  useEffect(() => { pendingLineRef.current = pendingLine; }, [pendingLine]);

  // Load saved channels.
  useEffect(() => {
    let cancelled = false;
    setMyChannels([]);
    if (!userId || !symbol || !context) return;
    fetchAdvancedDrawings(userId, symbol, context, TYPE)
      .then((rows) => {
        if (cancelled) return;
        setMyChannels(rows.map((r) => ({ id: r.id, ...r.points })));
      })
      .catch((e) => console.error("[chart_advanced_drawings-read]", e?.message || e));
    return () => { cancelled = true; };
  }, [userId, symbol, context]);

  const pointFromEvent = useCallback((e) => {
    const container = containerRef.current;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!container || !chart || !series) return null;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = series.coordinateToPrice(y);
    const time = xToTime(chart, x);
    if (price == null || time == null) return null;
    return { price, time };
  }, [chartRef, seriesRef, containerRef]);

  // Stage 1 — click-drag the main line. Only active in "idle" phase.
  useEffect(() => {
    const container = containerRef.current;
    if (!channelMode || !container || !userId || phase !== "idle") return;

    const handleMouseDown = (e) => {
      const p = pointFromEvent(e);
      if (!p) return;
      draggingRef.current = true;
      chartRef.current?.applyOptions({ handleScroll: false, handleScale: false });
      const next = { time1: p.time, price1: p.price, time2: p.time, price2: p.price };
      draftRef.current = next;
      setDraft(next);
    };
    const handleMouseMove = (e) => {
      if (!draggingRef.current) return;
      const p = pointFromEvent(e);
      if (!p) return;
      const prev = draftRef.current;
      if (!prev) return;
      const next = { ...prev, time2: p.time, price2: p.price };
      draftRef.current = next;
      setDraft(next);
    };
    const handleMouseUp = () => {
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
      if (!wasDragging) return;
      const finished = draftRef.current;
      draftRef.current = null;
      setDraft(null);
      if (!finished || (finished.time1 === finished.time2 && finished.price1 === finished.price2)) return;
      // Main line committed — move to stage 2, wait for the offset click. NOT persisted yet.
      setPendingLine(finished);
      setPhase("awaiting-offset");
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
    };
  }, [channelMode, phase, containerRef, chartRef, pointFromEvent, userId, symbol, context]);

  // Stage 2 — one more click sets the offset and completes the channel. Also tracks
  // mousemove (via pointFromEvent, same xToTime-backed conversion stage 1 uses) purely
  // for the live preview in offsetPreview — the click subscription is still what
  // actually commits the channel.
  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    const container = containerRef.current;
    if (!channelMode || phase !== "awaiting-offset" || !chart || !series || !container || !userId) return;

    const handleMouseMove = (e) => {
      const p = pointFromEvent(e);
      if (!p) return;
      setOffsetPreview(p);
    };

    const handleClick = (param) => {
      if (!param.point) return;
      const price3 = series.coordinateToPrice(param.point.y);
      const time3 = xToTime(chart, param.point.x);
      if (price3 == null || time3 == null) return;
      const line = pendingLineRef.current;
      if (!line) { setPhase("idle"); return; }
      const finished = { ...line, time3, price3 };
      insertAdvancedDrawing(userId, symbol, context, TYPE, finished)
        .then((row) => setMyChannels((prev) => [...prev, { id: row.id, ...row.points }]))
        .catch((e) => console.error("[chart_advanced_drawings-insert]", e?.message || e));
      setPendingLine(null);
      setOffsetPreview(null);
      setPhase("idle"); // ready for the next channel — multi-channel mode, like Trend Line
    };

    window.addEventListener("mousemove", handleMouseMove);
    chart.subscribeClick(handleClick);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      try { chart.unsubscribeClick(handleClick); } catch (e) { /* chart may already be torn down */ }
    };
  }, [channelMode, phase, chartRef, seriesRef, containerRef, pointFromEvent, userId, symbol, context]);

  // Reset mid-flight state if the tool is disarmed while a drag or offset-click is pending.
  useEffect(() => {
    if (!channelMode) {
      setPhase("idle");
      setPendingLine(null);
      setOffsetPreview(null);
      setDraft(null);
      draftRef.current = null;
      draggingRef.current = false;
    }
  }, [channelMode]);

  const deleteChannel = useCallback((id) => {
    if (!userId) return;
    deleteAdvancedDrawing(userId, id)
      .then(() => setMyChannels((prev) => prev.filter((c) => c.id !== id)))
      .catch((e) => console.error("[chart_advanced_drawings-delete]", e?.message || e));
  }, [userId]);

  // Re-project on pan/zoom — WATCHDOG version (2026-07-09): re-attaches when the chart
  // instance appears or is recreated, immediate tick on attach. See trendLineTool.js.
  useEffect(() => {
    if (myChannels.length === 0 && !draft && !pendingLine) return;
    const handler = () => setRangeTick((t) => t + 1);
    let subscribed = null;
    const ensure = () => {
      const chart = chartRef.current;
      if (chart === subscribed) return;
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
      subscribed = chart || null;
      if (chart) {
        try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler); handler(); } catch (e) { subscribed = null; }
      }
    };
    ensure();
    const watchdog = setInterval(ensure, 400);
    return () => {
      clearInterval(watchdog);
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
    };
  }, [chartRef, myChannels.length, draft, pendingLine]);

  // Derived: project each committed channel's two lines (main + constant-price-delta
  // offset) to pixel space; also project the in-progress stage-1 draft, or the pending
  // main line alone while awaiting the offset click (no live offset preview in v1).
  const projectedChannels = [];
  const chart = chartRef.current;
  const series = seriesRef.current;
  if (chart && series) {
    const projectLine = (t1, p1, t2, p2) => {
      const x1 = timeToX(chart, t1);
      const x2 = timeToX(chart, t2);
      const y1 = series.priceToCoordinate(p1);
      const y2 = series.priceToCoordinate(p2);
      if (x1 == null || x2 == null || y1 == null || y2 == null) return null;
      return { x1, y1, x2, y2 };
    };
    // Shared delta math: interpolate the line's price at the offset point's time, so the
    // second line stays a constant price-delta shift of the first (see this file's header
    // comment). Used both for committed channels (offset point = the stored 3rd click)
    // and the live stage-2 preview (offset point = the current mouse position).
    const computeOffsetLine = (line, pt) => {
      const span = line.time2 - line.time1;
      const frac = span !== 0 ? (pt.time - line.time1) / span : 0;
      const priceOnLine = line.price1 + (line.price2 - line.price1) * frac;
      const delta = pt.price - priceOnLine;
      return { price1: line.price1 + delta, price2: line.price2 + delta };
    };
    myChannels.forEach((c) => {
      const main = projectLine(c.time1, c.price1, c.time2, c.price2);
      if (!main) return;
      const off = computeOffsetLine(c, { time: c.time3, price: c.price3 });
      const offsetLine = projectLine(c.time1, off.price1, c.time2, off.price2);
      if (!offsetLine) return;
      projectedChannels.push({ id: c.id, main, offset: offsetLine, complete: true });
    });
    if (draft) {
      const main = projectLine(draft.time1, draft.price1, draft.time2, draft.price2);
      if (main) projectedChannels.push({ id: null, main, offset: null, complete: false, isDraft: true });
    }
    if (pendingLine && phase === "awaiting-offset") {
      const main = projectLine(pendingLine.time1, pendingLine.price1, pendingLine.time2, pendingLine.price2);
      if (main) {
        let offset = null;
        if (offsetPreview) {
          const off = computeOffsetLine(pendingLine, offsetPreview);
          offset = projectLine(pendingLine.time1, off.price1, pendingLine.time2, off.price2);
        }
        projectedChannels.push({ id: "pending", main, offset, complete: !!offset, isDraft: true });
      }
    }
  }

  return { channelMode, setChannelMode, myChannels, deleteChannel, projectedChannels, phase };
}

// SVG overlay — main line + offset line, with a faint fill polygon between them once both
// exist (mirrors the FVG band's translucent-fill visual language used elsewhere in this
// app). While a channel is incomplete (stage-1 draft, or stage-2 waiting on the offset
// click), only the main line renders. Indigo (#818cf8) — this codebase's crypto accent
// color (see App.js's isCrypto ? "#818cf8" : "#f97316" convention), distinct from Trend
// Line/Draw Line's stock-accent orange. pointerEvents: "none" on the SVG root — see
// measureTool.js's MeasureOverlay comment for why that's critical.
export function ChannelOverlay({ channels }) {
  if (!channels || channels.length === 0) return null;
  return (
    <svg
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 5, pointerEvents: "none" }}
    >
      {channels.map((c, i) => (
        <g key={c.id ?? `draft-${i}`} opacity={c.isDraft ? 0.5 : 1}>
          {c.complete && c.offset && (
            <polygon
              points={`${c.main.x1},${c.main.y1} ${c.main.x2},${c.main.y2} ${c.offset.x2},${c.offset.y2} ${c.offset.x1},${c.offset.y1}`}
              fill="rgba(129,140,248,0.08)"
            />
          )}
          <line x1={c.main.x1} y1={c.main.y1} x2={c.main.x2} y2={c.main.y2} stroke="#818cf8" strokeWidth="1.5" />
          {c.complete && c.offset && (
            <line x1={c.offset.x1} y1={c.offset.y1} x2={c.offset.x2} y2={c.offset.y2} stroke="#818cf8" strokeWidth="1.5" />
          )}
        </g>
      ))}
    </svg>
  );
}
